import { useState, useEffect, useRef, useCallback } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useForm } from "@tanstack/react-form";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { queryKeys } from "@/lib/queryKeys";
import { z } from "zod";
import { formatPrice } from "@printtat/shared";
import {
  ArrowLeft,
  ShieldCheck,
  Truck,
  CreditCard,
  Banknote,
  Receipt,
  Star,
  Phone,
  Plus,
  Check,
  X,
  Tag,
  ChevronDown,
  Loader2,
} from "lucide-react";
import { Button, Alert, IconButton, PageContent } from "@printtat/ui";
import { cartApi } from "@/services/cartApi";
import { discountApi } from "@/services/discountApi";
import type { DiscountValidationResult } from "@printtat/shared";
import { CartItemRow } from "@/modules/cart/components/CartItemRow";
import { AddressFormFields } from "@/components/forms/AddressForm";
import { useCartStore } from "@/stores/cartStore";
import { orderApi } from "@/services/orderApi";
import { paymentApi } from "@/services/paymentApi";
import { ApiError } from "@/lib/apiError";
import { addressApi } from "@/services/addressApi";
import { loadPaymobSdk, mountPaymobCheckout, getPaymobRedirectUrl } from "@/lib/paymob";
import type {
  CartValidationChange,
  CheckoutDto,
  PaymentMethod,
  SavedAddress,
  ShippingAddress,
} from "@printtat/shared";
import { cn } from "@/lib/utils";

const shippingSchema = z.object({
  fullName: z.string().min(1, "Full name is required"),
  phone: z.string().min(1, "Phone number is required"),
  addressLine1: z.string().min(1, "Address is required"),
  addressLine2: z.string().optional(),
  city: z.string().min(1, "City is required"),
  governorate: z.string().min(1, "Governorate is required"),
  postalCode: z.string().optional(),
});

// Compact address card used in address pickers
const AddressOption = ({
  addr,
  selected,
  onSelect,
  radioName,
}: {
  addr: SavedAddress;
  selected: boolean;
  onSelect: () => void;
  radioName: string;
}) => (
  <label
    className={`flex items-start gap-3 p-3.5 rounded-lg border-2 transition-colors cursor-pointer ${
      selected
        ? "border-gray-900 bg-gray-50"
        : "border-gray-200 hover:border-gray-300"
    }`}
  >
    <input
      type="radio"
      name={radioName}
      checked={selected}
      onChange={onSelect}
      className="accent-gray-900 mt-0.5"
    />
    <div className="flex-1 min-w-0">
      <div className="flex items-center gap-2">
        <span className="text-sm font-medium text-gray-900">{addr.label}</span>
        {addr.isDefault && (
          <span className="inline-flex items-center gap-0.5 text-[10px] font-medium text-amber-700 bg-amber-50 px-1.5 py-0.5 rounded-full">
            <Star className="w-2.5 h-2.5 fill-current" />
            Default
          </span>
        )}
      </div>
      <p className="text-sm text-gray-600 mt-0.5">{addr.fullName}</p>
      <p className="text-xs text-gray-500">
        {addr.addressLine1}
        {addr.addressLine2 && `, ${addr.addressLine2}`}, {addr.city},{" "}
        {addr.governorate}
      </p>
      <p className="text-xs text-gray-400 flex items-center gap-1 mt-0.5">
        <Phone className="w-2.5 h-2.5" />
        {addr.phone}
      </p>
    </div>
    {selected && (
      <Check className="w-4 h-4 text-gray-900 mt-0.5 flex-shrink-0" />
    )}
  </label>
);

const Skeleton = ({ className = "" }: { className?: string }) => (
  <div className={`animate-pulse bg-gray-200 rounded-md ${className}`} />
);

const CheckoutSkeleton = () => (
  <PageContent>
    <div className="flex flex-col lg:flex-row gap-6">
      {/* Left column */}
      <div className="flex-1 min-w-0 space-y-3">
        {/* Header */}
        <div className="flex items-start gap-3 mb-8">
          <Skeleton className="w-8 h-8 rounded-lg" />
          <div>
            <Skeleton className="w-32 h-6 mb-1" />
            <Skeleton className="w-24 h-4" />
          </div>
        </div>

        {/* Shipping Address */}
        <div className="bg-white rounded-xl border border-gray-200 p-6">
          <div className="flex items-center justify-between mb-5">
            <div className="flex items-center gap-2">
              <Skeleton className="w-4 h-4" />
              <Skeleton className="w-32 h-4" />
            </div>
            <Skeleton className="w-20 h-8 rounded-lg" />
          </div>
          <div className="space-y-2">
            <Skeleton className="w-full h-20 rounded-lg" />
            <Skeleton className="w-full h-20 rounded-lg" />
          </div>
        </div>

        {/* Billing Address */}
        <div className="bg-white rounded-xl border border-gray-200 p-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Skeleton className="w-4 h-4" />
              <Skeleton className="w-28 h-4" />
            </div>
            <Skeleton className="w-36 h-5 rounded-full" />
          </div>
        </div>

        {/* Payment + Notes */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <div className="bg-white rounded-xl border border-gray-200 p-6">
            <div className="flex items-center gap-2 mb-5">
              <Skeleton className="w-4 h-4" />
              <Skeleton className="w-28 h-4" />
            </div>
            <div className="space-y-3">
              <Skeleton className="w-full h-16 rounded-lg" />
              <Skeleton className="w-full h-16 rounded-lg" />
            </div>
          </div>
          <div className="bg-white rounded-xl border border-gray-200 p-6">
            <Skeleton className="w-24 h-4 mb-4" />
            <Skeleton className="w-full h-28 rounded-md" />
          </div>
        </div>
      </div>

      {/* Right column — Order Summary */}
      <div className="w-full lg:w-[360px] flex-shrink-0">
        <div className="bg-white rounded-xl border border-gray-200 p-6">
          <Skeleton className="w-28 h-4 mb-4" />
          <div className="space-y-2 mb-4">
            <div className="flex justify-between">
              <Skeleton className="w-16 h-4" />
              <Skeleton className="w-20 h-4" />
            </div>
            <div className="flex justify-between">
              <Skeleton className="w-16 h-4" />
              <Skeleton className="w-32 h-4" />
            </div>
            <div className="flex justify-between pt-2 border-t border-gray-100">
              <Skeleton className="w-12 h-5" />
              <Skeleton className="w-20 h-5" />
            </div>
          </div>
          <Skeleton className="w-full h-10 rounded-lg mb-5" />
          <div className="space-y-3 border-t border-gray-100 pt-4">
            <Skeleton className="w-16 h-3" />
            {[1, 2].map((i) => (
              <div key={i} className="flex gap-3">
                <Skeleton className="w-12 h-12 rounded-lg" />
                <div className="flex-1">
                  <Skeleton className="w-24 h-4 mb-1" />
                  <Skeleton className="w-16 h-3" />
                </div>
                <Skeleton className="w-16 h-4" />
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  </PageContent>
);

export const CheckoutPage = () => {
  const navigate = useNavigate();
  const { items, itemCount, subtotal, fetchCart } = useCartStore();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [sameAsBilling, setSameAsBilling] = useState(true);
  const [billingAddressId, setBillingAddressId] = useState<string | null>(null);
  const [notes, setNotes] = useState("");
  const [paymentMethod, setPaymentMethod] = useState<PaymentMethod>("cod");
  const [discountCode, setDiscountCode] = useState("");
  const [discountResult, setDiscountResult] = useState<DiscountValidationResult | null>(null);
  const [isValidatingDiscount, setIsValidatingDiscount] = useState(false);
  const [discountError, setDiscountError] = useState<string | null>(null);
  const [promoOpen, setPromoOpen] = useState(false);
  const checkoutCompleteRef = useRef(false);

  // Online payment state
  const [paymentOrderId, setPaymentOrderId] = useState<string | null>(null);
  const [paymentStep, setPaymentStep] = useState<
    'idle' | 'initiating' | 'checkout' | 'polling' | 'success' | 'failed' | 'timeout'
  >('idle');
  const [paymentError, setPaymentError] = useState<string | null>(null);
  const pollTimerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const pollTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const paymobMountedRef = useRef(false);

  // Page loading state
  const [pageReady, setPageReady] = useState(false);

  // Cart validation state
  const [cartIssues, setCartIssues] = useState<CartValidationChange[]>([]);
  const [isValidating, setIsValidating] = useState(false);

  const queryClient = useQueryClient();

  // Address state — useQuery for fetching, local state for selection
  const { data: savedAddresses = [], isFetched: addressesLoaded } = useQuery({
    queryKey: queryKeys.addresses.all(),
    queryFn: () => addressApi.getAddresses(),
    staleTime: 30_000,
  });
  const [selectedAddressId, setSelectedAddressId] = useState<string | null>(
    null,
  );
  const [showNewAddressForm, setShowNewAddressForm] = useState(false);
  const [isSavingAddress, setIsSavingAddress] = useState(false);
  const [newAddressLabel, setNewAddressLabel] = useState("Home");

  const count = itemCount();
  const total = subtotal();
  const codAvailable = count <= 1;

  // Load everything on mount — cart + validate + addresses in parallel
  const didLoadRef = useRef(false);
  useEffect(() => {
    if (didLoadRef.current) return;
    didLoadRef.current = true;

    const loadCart = async () => {
      await fetchCart();

      setIsValidating(true);
      try {
        const result = await cartApi.validate();
        setCartIssues(result.changes);

        const priceChanges = result.changes.filter(
          (c) => c.type === "price_changed",
        );
        if (priceChanges.length > 0) {
          useCartStore.setState((state) => ({
            items: state.items.map((item) => {
              const change = priceChanges.find(
                (c) => c.itemId === item.id,
              );
              return change?.newPrice != null
                ? { ...item, stalePriceFrom: item.unitPrice, unitPrice: change.newPrice, isStale: true }
                : item;
            }),
          }));
        }
      } catch {
        // Silently fail — checkout will catch issues
      } finally {
        setIsValidating(false);
      }
    };

    const minDelay = new Promise((r) => setTimeout(r, 1000));
    Promise.all([loadCart(), minDelay]).then(() => {
      // Redirect if cart is empty after loading
      const currentItems = useCartStore.getState().items;
      if (currentItems.length === 0 && !checkoutCompleteRef.current) {
        navigate("/dashboard", { replace: true });
      }
      setPageReady(true);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const cartBlockers = cartIssues.filter((c) => c.type !== "price_changed");
  const hasCartBlockers = cartBlockers.length > 0;

  // Redirect if cart becomes empty (e.g. after removing items)
  useEffect(() => {
    if (
      pageReady &&
      !checkoutCompleteRef.current &&
      items.length === 0
    ) {
      navigate("/dashboard", { replace: true });
    }
  }, [pageReady, items.length, navigate]);

  // Auto-select default address when addresses load
  useEffect(() => {
    if (savedAddresses.length > 0 && !selectedAddressId) {
      const defaultAddr = savedAddresses.find((a) => a.isDefault);
      const defaultId = defaultAddr ? defaultAddr.id : savedAddresses[0].id;
      setSelectedAddressId(defaultId);
      setBillingAddressId(defaultId);
    }
  }, [savedAddresses, selectedAddressId]);

  // Auto-select payment method based on item count
  useEffect(() => {
    if (!codAvailable && paymentMethod === "cod") {
      setPaymentMethod("online");
    }
  }, [codAvailable, paymentMethod]);

  // Cleanup polling on unmount
  useEffect(() => {
    return () => {
      if (pollTimerRef.current) clearInterval(pollTimerRef.current);
      if (pollTimeoutRef.current) clearTimeout(pollTimeoutRef.current);
    };
  }, []);

  const stopPolling = useCallback(() => {
    if (pollTimerRef.current) {
      clearInterval(pollTimerRef.current);
      pollTimerRef.current = null;
    }
    if (pollTimeoutRef.current) {
      clearTimeout(pollTimeoutRef.current);
      pollTimeoutRef.current = null;
    }
  }, []);

  const startPolling = useCallback(
    (orderId: string) => {
      setPaymentStep('polling');

      const poll = async () => {
        try {
          const status = await paymentApi.getStatus(orderId);
          if (status.paymentStatus === 'paid') {
            stopPolling();
            setPaymentStep('success');
            checkoutCompleteRef.current = true;
            useCartStore.setState({ items: [] });
            navigate(`/orders/${orderId}`, { replace: true });
          } else if (status.paymentStatus === 'failed') {
            stopPolling();
            setPaymentStep('failed');
            setPaymentError('Payment was not successful. Please try again.');
          }
        } catch {
          // Silently continue polling
        }
      };

      // Poll every 3 seconds
      pollTimerRef.current = setInterval(poll, 3000);
      // Start immediately
      poll();

      // 10 minute timeout
      pollTimeoutRef.current = setTimeout(() => {
        stopPolling();
        setPaymentStep('timeout');
      }, 10 * 60 * 1000);
    },
    [navigate, stopPolling],
  );

  // Form for creating a new address inline
  const addressForm = useForm({
    defaultValues: {
      fullName: "",
      phone: "",
      addressLine1: "",
      addressLine2: "",
      city: "",
      governorate: "",
      postalCode: "",
    },
    onSubmit: async ({ value }) => {
      setIsSavingAddress(true);
      setSubmitError(null);

      try {
        const parsed = shippingSchema.parse(value);
        const saved = await addressApi.createAddress({
          label: newAddressLabel || "Home",
          ...parsed,
          addressLine2: parsed.addressLine2 || undefined,
          postalCode: parsed.postalCode || undefined,
        });

        await queryClient.invalidateQueries({ queryKey: queryKeys.addresses.all() });
        setSelectedAddressId(saved.id);
        if (sameAsBilling) setBillingAddressId(saved.id);
        setShowNewAddressForm(false);
        setNewAddressLabel("Home");
        addressForm.reset();
      } catch (error) {
        if (error instanceof z.ZodError) {
          setSubmitError(error.issues.map((i) => i.message).join(", "));
        } else {
          setSubmitError(
            error instanceof Error ? error.message : "Failed to save address",
          );
        }
      } finally {
        setIsSavingAddress(false);
      }
    },
  });

  // Helper to convert SavedAddress → ShippingAddress
  const toShippingAddress = (addr: SavedAddress): ShippingAddress => ({
    fullName: addr.fullName,
    phone: addr.phone,
    addressLine1: addr.addressLine1,
    addressLine2: addr.addressLine2 || undefined,
    city: addr.city,
    governorate: addr.governorate,
    postalCode: addr.postalCode || undefined,
  });

  // Place order handler
  const handlePlaceOrder = async () => {
    setIsSubmitting(true);
    setSubmitError(null);
    setPaymentError(null);

    try {
      if (!selectedAddressId) {
        throw new Error("Please select or add a shipping address");
      }

      const shippingAddr = savedAddresses.find(
        (a) => a.id === selectedAddressId,
      );
      if (!shippingAddr) throw new Error("Selected address not found");

      const shippingAddress = toShippingAddress(shippingAddr);

      const dto: CheckoutDto = {
        shippingAddress,
        paymentMethod,
        notes: notes || undefined,
        discountCode: discountResult?.valid ? discountResult.code : undefined,
      };

      if (!sameAsBilling && billingAddressId) {
        const billingAddr = savedAddresses.find(
          (a) => a.id === billingAddressId,
        );
        if (billingAddr) {
          dto.billingAddress = toShippingAddress(billingAddr);
        }
      }

      const result = await orderApi.checkout(dto);

      if (paymentMethod === "online") {
        // Online: initiate payment → mount Paymob checkout → poll
        setPaymentOrderId(result.orderId);
        setPaymentStep('initiating');
        paymobMountedRef.current = false;

        const initResult = await paymentApi.initiate(result.orderId);

        // Brand-new order has no prior payments, so alreadyPaid shouldn't
        // happen here; defensively navigate if it does.
        if ('alreadyPaid' in initResult) {
          checkoutCompleteRef.current = true;
          useCartStore.setState({ items: [] });
          navigate(`/orders/${result.orderId}`, { replace: true });
          return;
        }
        const { clientSecret } = initResult;

        setPaymentStep('checkout');

        // Load SDK and mount
        try {
          await loadPaymobSdk();
          // Wait for next render to ensure the mount target exists
          requestAnimationFrame(() => {
            try {
              mountPaymobCheckout(clientSecret, '#paymob-checkout');
              paymobMountedRef.current = true;
              startPolling(result.orderId);
            } catch {
              // SDK mount failed — use redirect fallback
              window.location.href = getPaymobRedirectUrl(clientSecret);
            }
          });
        } catch {
          // SDK load failed — use redirect fallback
          window.location.href = getPaymobRedirectUrl(clientSecret);
        }
      } else {
        // COD: clear cart and navigate directly
        checkoutCompleteRef.current = true;
        useCartStore.setState({ items: [] });
        navigate(`/orders/${result.orderId}`, { replace: true });
      }
    } catch (error) {
      setSubmitError(
        error instanceof Error ? error.message : "Checkout failed",
      );
      setPaymentStep('idle');
      setPaymentOrderId(null);
    } finally {
      setIsSubmitting(false);
    }
  };

  // Retry payment for failed/timeout
  const handleRetryPayment = async () => {
    if (!paymentOrderId) return;
    setPaymentError(null);
    setPaymentStep('initiating');
    paymobMountedRef.current = false;

    try {
      const retryResult = await paymentApi.initiate(paymentOrderId);

      // Reconciliation recovered the prior attempt — navigate to the order.
      if ('alreadyPaid' in retryResult) {
        useCartStore.setState({ items: [] });
        navigate(`/orders/${paymentOrderId}`, { replace: true });
        return;
      }
      const { clientSecret } = retryResult;
      setPaymentStep('checkout');

      try {
        await loadPaymobSdk();
        requestAnimationFrame(() => {
          try {
            mountPaymobCheckout(clientSecret, '#paymob-checkout');
            paymobMountedRef.current = true;
            startPolling(paymentOrderId);
          } catch {
            window.location.href = getPaymobRedirectUrl(clientSecret);
          }
        });
      } catch {
        window.location.href = getPaymobRedirectUrl(clientSecret);
      }
    } catch (error) {
      // Customer hit the 3-attempt cap inside the checkout overlay. Send
      // them to the order detail page where the Re-order CTA lives — staying
      // here would just show a raw error string.
      if (
        error instanceof ApiError &&
        error.code === 'PAYMENT_RETRY_LIMIT' &&
        paymentOrderId
      ) {
        navigate(`/orders/${paymentOrderId}`, { replace: true });
        return;
      }
      setPaymentError(
        error instanceof Error ? error.message : "Failed to initiate payment",
      );
      setPaymentStep('failed');
    }
  };

  // Keep billing synced when "same as shipping" is on
  const handleShippingSelect = (id: string) => {
    setSelectedAddressId(id);
    if (sameAsBilling) setBillingAddressId(id);
  };

  const handleSameAsBillingToggle = (checked: boolean) => {
    setSameAsBilling(checked);
    if (checked) setBillingAddressId(selectedAddressId);
  };

  const selectedAddress = savedAddresses.find(
    (a) => a.id === selectedAddressId,
  );
  const billingAddress = savedAddresses.find(
    (a) => a.id === billingAddressId,
  );

  const hasAddresses = savedAddresses.length > 0;
  const formVisible =
    showNewAddressForm || (addressesLoaded && !hasAddresses);

  if (!pageReady) {
    return <CheckoutSkeleton />;
  }

  return (
    <PageContent>


      <div className="flex flex-col lg:flex-row gap-6">
        
        {/* Left Column — all form sections stacked */}
        <div className="flex-1 min-w-0 space-y-3">
           {/* Header */}
      <div className="flex items-start gap-3 mb-8">
        <IconButton
          size="sm"
          onClick={() => navigate(-1)}
        >
          <ArrowLeft className="w-4 h-4 text-gray-600" />
        </IconButton>
        <div>
          <h1 className="text-xl font-semibold text-gray-900">Checkout</h1>
          <p className="text-sm text-gray-500">
            {count} {count === 1 ? "item" : "items"} in your order
          </p>
        </div>
      </div>
          {/* Shipping Address */}
          <section className="bg-white rounded-xl border border-gray-200 p-6">
            <div className="flex items-center justify-between mb-5">
              <div className="flex items-center gap-2">
                <Truck className="w-4 h-4 text-gray-700" />
                <h2 className="text-sm font-semibold text-gray-900">
                  Shipping Address
                </h2>
              </div>
              {hasAddresses && !showNewAddressForm && (
                <div className="flex items-center gap-1">
                  <Link
                    to="/settings/addresses"
                    className="text-xs text-gray-500 hover:text-gray-900 transition-colors px-2 py-1"
                  >
                    Manage Addresses
                  </Link>
                  <Button
                    size="sm"
                    onClick={() => setShowNewAddressForm(true)}
                  >
                    <Plus className="w-3.5 h-3.5 mr-1" />
                    Add New
                  </Button>
                </div>
              )}
            </div>

            {/* Saved address list */}
            {hasAddresses && !showNewAddressForm && (
              <div className="space-y-2">
                {savedAddresses.map((addr) => (
                  <AddressOption
                    key={addr.id}
                    addr={addr}
                    selected={selectedAddressId === addr.id}
                    onSelect={() => handleShippingSelect(addr.id)}
                    radioName="shippingAddress"
                  />
                ))}
              </div>
            )}

            {/* Inline new address form */}
            {formVisible && (
              <div
                className={
                  hasAddresses ? "border-t border-gray-100 pt-5 mt-2" : ""
                }
              >
                {hasAddresses && (
                  <h3 className="text-sm font-medium text-gray-900 mb-4">
                    New Address
                  </h3>
                )}
                <form
                  onSubmit={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    addressForm.handleSubmit();
                  }}
                  className="space-y-4"
                >
                  <div className="max-w-xs">
                    <label className="block text-xs font-medium text-gray-700 mb-1.5">
                      Label <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-gray-900 focus:border-transparent"
                      placeholder='e.g. "Home", "Office"'
                      value={newAddressLabel}
                      onChange={(e) => setNewAddressLabel(e.target.value)}
                    />
                  </div>

                  <AddressFormFields form={addressForm} />

                  <div className="flex justify-end gap-3 pt-2">
                    {hasAddresses && (
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={() => {
                          setShowNewAddressForm(false);
                          addressForm.reset();
                          setNewAddressLabel("Home");
                          setSubmitError(null);
                        }}
                        disabled={isSavingAddress}
                      >
                        Cancel
                      </Button>
                    )}
                    <Button
                      type="submit"
                      size="sm"
                      loading={isSavingAddress}
                    >
                      Save Address
                    </Button>
                  </div>
                </form>
              </div>
            )}
          </section>

          {/* Billing Address */}
          <section className="bg-white rounded-xl border border-gray-200 p-6">
            <div className={cn("flex items-center justify-between", sameAsBilling ? "mb-0" : "mb-5")}>
              <div className="flex items-center gap-2">
                <Receipt className="w-4 h-4 text-gray-700" />
                <h2 className="text-sm font-semibold text-gray-900">
                  Billing Address
                </h2>
              </div>
              <label className="flex items-center gap-2 text-sm text-gray-600 cursor-pointer">
                <span>Same as shipping</span>
                <button
                  type="button"
                  role="switch"
                  aria-checked={sameAsBilling}
                  onClick={() => handleSameAsBillingToggle(!sameAsBilling)}
                  className={`relative inline-flex h-5 w-9 items-center rounded-full transition-colors ${
                    sameAsBilling ? "bg-gray-900" : "bg-gray-300"
                  }`}
                >
                  <span
                    className={`inline-block h-3.5 w-3.5 rounded-full bg-white transition-transform ${
                      sameAsBilling ? "translate-x-[18px]" : "translate-x-[3px]"
                    }`}
                  />
                </button>
              </label>
            </div>

            {!sameAsBilling && hasAddresses && (
              <div className="space-y-2">
                {savedAddresses.map((addr) => (
                  <AddressOption
                    key={addr.id}
                    addr={addr}
                    selected={billingAddressId === addr.id}
                    onSelect={() => setBillingAddressId(addr.id)}
                    radioName="billingAddress"
                  />
                ))}
              </div>
            )}

            {!sameAsBilling && !hasAddresses && (
              <p className="text-sm text-gray-500">
                Save a shipping address first to select a billing address.
              </p>
            )}
          </section>

          {/* Payment Method + Order Notes side by side */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {/* Payment Method */}
            <section className="bg-white rounded-xl border border-gray-200 p-6">
              <div className="flex items-center gap-2 mb-5">
                <ShieldCheck className="w-4 h-4 text-gray-700" />
                <h2 className="text-sm font-semibold text-gray-900">
                  Payment Method
                </h2>
              </div>

              <div className="space-y-3">
                <label
                  className={`flex items-center gap-3 p-4 rounded-lg border-2 transition-colors cursor-pointer ${
                    paymentMethod === "cod"
                      ? "border-gray-900 bg-gray-50"
                      : codAvailable
                        ? "border-gray-200 hover:border-gray-300"
                        : "border-gray-100 bg-gray-50 opacity-50 cursor-not-allowed"
                  }`}
                >
                  <input
                    type="radio"
                    name="paymentMethod"
                    value="cod"
                    checked={paymentMethod === "cod"}
                    onChange={() => setPaymentMethod("cod")}
                    disabled={!codAvailable}
                    className="accent-gray-900"
                  />
                  <Banknote className="w-5 h-5 text-gray-600" />
                  <div className="flex-1">
                    <p className="text-sm font-medium text-gray-900">
                      Cash on Delivery
                    </p>
                    <p className="text-xs text-gray-500">
                      {codAvailable
                        ? "Pay when you receive your order"
                        : "Only available for single-item orders"}
                    </p>
                  </div>
                </label>

                <label
                  className={`flex items-center gap-3 p-4 rounded-lg border-2 transition-colors cursor-pointer ${
                    paymentMethod === "online"
                      ? "border-gray-900 bg-gray-50"
                      : "border-gray-200 hover:border-gray-300"
                  }`}
                >
                  <input
                    type="radio"
                    name="paymentMethod"
                    value="online"
                    checked={paymentMethod === "online"}
                    onChange={() => setPaymentMethod("online")}
                    className="accent-gray-900"
                  />
                  <CreditCard className="w-5 h-5 text-gray-600" />
                  <div className="flex-1">
                    <p className="text-sm font-medium text-gray-900">
                      Online Payment
                    </p>
                    <p className="text-xs text-gray-500">
                      Card, mobile wallet, or Fawry
                    </p>
                  </div>
                </label>
              </div>
            </section>

            {/* Order Notes */}
            <section className="bg-white rounded-xl border border-gray-200 p-6">
              <h2 className="text-sm font-semibold text-gray-900 mb-4">
                Order Notes
              </h2>
              <textarea
                className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-gray-900 focus:border-transparent resize-none"
                placeholder="Any special instructions for your order (optional)"
                rows={5}
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
              />
            </section>
          </div>

          {submitError && (
            <Alert variant="error">{submitError}</Alert>
          )}

          {/* Submit button — mobile only */}
          <div className="lg:hidden">
            <Button
              type="button"
              className="w-full"
              disabled={isSubmitting || !selectedAddressId || formVisible || hasCartBlockers || isValidating}
              loading={isSubmitting}
              onClick={handlePlaceOrder}
            >
              Place Order
            </Button>
          </div>
        </div>

        {/* Right Column — Order Summary */}
        <div className="w-full lg:w-[360px] flex-shrink-0">
          <div className="lg:sticky lg:top-6">
            <div className="bg-white rounded-xl border border-gray-200 p-6">
              <h2 className="text-sm font-semibold text-gray-900 mb-4">
                Order Summary
              </h2>

              {/* Promo code */}
              <div className="mb-4 pb-4 border-b border-gray-100">
                {discountResult?.valid ? (
                  <div className="flex items-center gap-2">
                    <Tag size={14} className="text-gray-400" />
                    <span className="inline-flex items-center gap-1.5 text-sm font-mono font-medium text-gray-700">
                      {discountResult.code}
                      <button
                        type="button"
                        onClick={() => {
                          setDiscountResult(null);
                          setDiscountCode("");
                          setDiscountError(null);
                        }}
                        className="text-gray-400 hover:text-gray-600 transition-colors"
                      >
                        <X size={14} />
                      </button>
                    </span>
                  </div>
                ) : (
                  <div>
                    <button
                      type="button"
                      onClick={() => setPromoOpen(!promoOpen)}
                      className="flex items-center gap-2 text-sm text-gray-500 hover:text-gray-700 transition-colors w-full"
                    >
                      <Tag size={14} />
                      <span>Have a promo code?</span>
                      <ChevronDown size={14} className={`ml-auto transition-transform ${promoOpen ? 'rotate-180' : ''}`} />
                    </button>
                    {promoOpen && (
                      <div className="mt-3">
                        <div className="flex gap-2">
                          <input
                            type="text"
                            value={discountCode}
                            onChange={(e) => {
                              setDiscountCode(e.target.value.toUpperCase());
                              setDiscountError(null);
                            }}
                            placeholder="Enter code"
                            className="flex-1 px-3 py-2 text-sm border rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-gray-200 font-mono"
                          />
                          <Button
                            variant="outline"
                            size="sm"
                            disabled={!discountCode.trim() || isValidatingDiscount}
                            loading={isValidatingDiscount}
                            onClick={async () => {
                              setIsValidatingDiscount(true);
                              setDiscountError(null);
                              try {
                                const result = await discountApi.validate(discountCode.trim());
                                if (result.valid) {
                                  setDiscountResult(result);
                                  setDiscountError(null);
                                  setPromoOpen(false);
                                } else {
                                  setDiscountError(result.error ?? "Invalid code");
                                }
                              } catch (err) {
                                setDiscountError(
                                  err instanceof Error ? err.message : "Failed to validate code",
                                );
                              } finally {
                                setIsValidatingDiscount(false);
                              }
                            }}
                          >
                            Apply
                          </Button>
                        </div>
                        {discountError && (
                          <p className="text-xs text-red-500 mt-1.5">{discountError}</p>
                        )}
                      </div>
                    )}
                  </div>
                )}
              </div>

              {/* Totals */}
              <div className="space-y-2 mb-4">
                <div className="flex justify-between text-sm">
                  <span className="text-gray-500">Subtotal ({count} {count === 1 ? 'item' : 'items'})</span>
                  <span className="text-gray-900">
                    {formatPrice(total)}
                  </span>
                </div>
                {discountResult?.valid && !discountResult.freeShipping && (
                  <div className="flex justify-between text-sm">
                    <span className="text-green-600">
                      Discount
                      <span className="text-gray-400 font-normal ml-1">
                        {discountResult.type === 'percentage'
                          ? `${discountResult.value}% off${discountResult.maxDiscountAmount != null && discountResult.discountAmount >= discountResult.maxDiscountAmount ? ` · capped at ${formatPrice(discountResult.maxDiscountAmount)}` : ''}`
                          : discountResult.type === 'fixed_amount'
                            ? `${formatPrice(discountResult.value)} off`
                            : ''}
                      </span>
                    </span>
                    <span className="text-green-600">
                      −{formatPrice(discountResult.discountAmount)}
                    </span>
                  </div>
                )}
                <div className="flex justify-between text-sm">
                  {discountResult?.freeShipping ? (
                    <>
                      <span className="text-green-600">Discounted shipping</span>
                      <span className="text-green-600">Free shipping</span>
                    </>
                  ) : (
                    <>
                      <span className="text-gray-500">Shipping</span>
                      <span className="text-gray-500 text-xs">Calculated after confirmation</span>
                    </>
                  )}
                </div>
                <div className="flex justify-between text-sm font-semibold pt-2 border-t border-gray-100">
                  <span className="text-gray-900">Total</span>
                  <span className="text-gray-900">
                    {formatPrice(
                      total - (discountResult?.valid ? discountResult.discountAmount : 0),
                    )}
                  </span>
                </div>
              </div>

              {/* Desktop submit */}
              <div className="hidden lg:block mb-5">
                <Button
                  type="button"
                  className="w-full"
                  disabled={isSubmitting || !selectedAddressId || formVisible || hasCartBlockers || isValidating}
                  loading={isSubmitting}
                  onClick={handlePlaceOrder}
                >
                  Place Order
                </Button>
              </div>

              {/* Shipping to summary */}
              {selectedAddress && !formVisible && (
                <div className="mb-4 pb-4 border-t border-gray-100 pt-4">
                  <p className="text-xs font-medium text-gray-500 mb-1">
                    Shipping to
                  </p>
                  <p className="text-sm font-medium text-gray-900">
                    {selectedAddress.label}
                  </p>
                  <p className="text-sm text-gray-600">
                    {selectedAddress.fullName}
                  </p>
                  <p className="text-xs text-gray-500">
                    {selectedAddress.addressLine1}, {selectedAddress.city},{" "}
                    {selectedAddress.governorate}
                  </p>
                </div>
              )}

              {/* Billing to summary (only when different) */}
              {!sameAsBilling &&
                billingAddress &&
                billingAddressId !== selectedAddressId && (
                  <div className="mb-4 pb-4 border-b border-gray-100">
                    <p className="text-xs font-medium text-gray-500 mb-1">
                      Billing to
                    </p>
                    <p className="text-sm font-medium text-gray-900">
                      {billingAddress.label}
                    </p>
                    <p className="text-sm text-gray-600">
                      {billingAddress.fullName}
                    </p>
                    <p className="text-xs text-gray-500">
                      {billingAddress.addressLine1}, {billingAddress.city},{" "}
                      {billingAddress.governorate}
                    </p>
                  </div>
                )}

              {/* Items */}
              <div className="divide-y divide-gray-100 border-t border-gray-100">
                <p className="text-xs font-medium text-gray-500 pt-4 pb-1">
                  {count} {count === 1 ? "item" : "items"}
                </p>
                {items.map((item) => (
                  <CartItemRow
                    key={item.id}
                    item={item}
                    compact
                  />
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Online Payment Overlay */}
      {paymentStep !== 'idle' && (
        <div className="fixed inset-0 z-50 bg-white/95 flex items-center justify-center">
          <div className="w-full max-w-lg mx-auto px-6 text-center">
            {paymentStep === 'initiating' && (
              <div className="space-y-4">
                <Loader2 className="w-8 h-8 animate-spin text-gray-400 mx-auto" />
                <p className="text-sm text-gray-600">Preparing payment...</p>
              </div>
            )}

            {(paymentStep === 'checkout' || paymentStep === 'polling') && (
              <div className="space-y-6">
                <div>
                  <h2 className="text-lg font-semibold text-gray-900 mb-1">
                    Complete Your Payment
                  </h2>
                  <p className="text-sm text-gray-500">
                    Secure payment powered by Paymob
                  </p>
                </div>

                {/* Paymob checkout mounts here */}
                <div id="paymob-checkout" className="min-h-15" />

                {paymentStep === 'polling' && (
                  <div className="flex items-center justify-center gap-2 text-sm text-gray-500">
                    <Loader2 className="w-4 h-4 animate-spin" />
                    <span>Waiting for payment confirmation...</span>
                  </div>
                )}
              </div>
            )}

            {paymentStep === 'failed' && (
              <div className="space-y-4">
                <div className="w-12 h-12 rounded-full bg-red-100 flex items-center justify-center mx-auto">
                  <X className="w-6 h-6 text-red-600" />
                </div>
                <div>
                  <h2 className="text-lg font-semibold text-gray-900 mb-1">
                    Payment Failed
                  </h2>
                  <p className="text-sm text-gray-500">
                    {paymentError || 'Something went wrong with your payment.'}
                  </p>
                </div>
                <div className="flex gap-3 justify-center">
                  <Button variant="outline" onClick={() => {
                    setPaymentStep('idle');
                    setPaymentOrderId(null);
                    setPaymentError(null);
                  }}>
                    Back to Checkout
                  </Button>
                  <Button onClick={handleRetryPayment}>
                    Try Again
                  </Button>
                </div>
              </div>
            )}

            {paymentStep === 'timeout' && (
              <div className="space-y-4">
                <div className="w-12 h-12 rounded-full bg-amber-100 flex items-center justify-center mx-auto">
                  <Loader2 className="w-6 h-6 text-amber-600" />
                </div>
                <div>
                  <h2 className="text-lg font-semibold text-gray-900 mb-1">
                    Payment is Taking Longer Than Expected
                  </h2>
                  <p className="text-sm text-gray-500">
                    Your payment may still be processing. You can check your order for updates.
                  </p>
                </div>
                <div className="flex gap-3 justify-center">
                  <Button
                    variant="outline"
                    onClick={() => navigate(`/orders/${paymentOrderId}`, { replace: true })}
                  >
                    View Order
                  </Button>
                  <Button onClick={handleRetryPayment}>
                    Try Again
                  </Button>
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </PageContent>
  );
};
